import { useEffect, useState } from "react";
import { ImagePlus } from "lucide-react";
import { api } from "@/lib/api";

export default function SelectedWork() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portfolio", { params: { limit: 8 } })
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="selected-work" className="relative bg-[#F4F4F0] text-black border-t border-black">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 mb-12">
          <div className="md:col-span-6">
            <div className="flex items-center gap-4 mb-6">
              <span className="w-10 h-px bg-black" />
              <span className="font-mono tracking-[0.22em] uppercase">Selected Work</span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-tight">
              Real work. <em className="italic">Fresh off the press.</em>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p className="text-base md:text-lg text-black/80 leading-relaxed max-w-[52ch]">
              A growing archive of finished jobs, samples and production work from Arvind Printers.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="border border-dashed border-black/35 min-h-[260px] grid place-items-center text-center px-6">
            <div className="font-mono tracking-[0.18em] uppercase text-black/55">Loading recent work…</div>
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-black/35 min-h-[300px] grid place-items-center text-center px-6 bg-white/20">
            <div>
              <ImagePlus className="w-8 h-8 mx-auto mb-4 opacity-55" />
              <div className="font-display text-3xl md:text-4xl">Our work archive is being prepared.</div>
              <p className="mt-3 text-black/65 max-w-xl mx-auto">
                New print projects, samples and finished jobs will appear here as we publish them from our studio.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {items.map((item) => (
              <article key={item.id} className="border border-black bg-white/25 overflow-hidden group">
                <div className="aspect-[4/5] bg-black/5 overflow-hidden">
                  {item.cover?.url ? (
                    <img
                      src={item.cover.url}
                      alt={item.title || "Arvind Printers project"}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-black/30"><ImagePlus /></div>
                  )}
                </div>
                <div className="p-4">
                  <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-black/55">
                    {item.category}{item.client ? ` · ${item.client}` : ""}
                  </div>
                  <h3 className="font-display text-2xl mt-1 leading-tight">{item.title}</h3>
                  {item.description ? <p className="mt-2 text-sm leading-relaxed text-black/65 line-clamp-3">{item.description}</p> : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
